import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Drawer, Button, Table, Typography, Tabs, Pagination, message, Modal, Input, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { t } from '../../../../i18n';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';
import {
  GET_AREAS_FOR_POD_REVIEW,
  GetAreasForPodReviewResponse,
  GetAreasForPodReviewVariables,
} from '../../../qualitycontrol/podReview/hooks/usePodReviewOptions';
import { SiteType } from '../../../../graphql/types/SiteType';
import { overallStatusOptions } from '../../../../config/filterOptions';
import { OverallStatus } from '../../../../enums/DeliveryEnum';
import DateTimeRangeFilterItem from '../../components/DateTimeRangeFilterItem';
import BatchInputFilterItem from '../../components/BatchInputFilterItem';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import ParcelFilterStyle from '../../components/ParcelFilterStyle';
import { useCurrentDsp } from '../../../../hooks/useCurrentDsp';
import {
  LIST_ORDER_POOL_PAGE,
  LIST_ORDER_POOL_PAGE_FOR_DSP,
  ListOrderPoolPageVariables,
  OrderPoolPageItem,
} from '../graphql/routePlanningOrderPool';
import {
  BATCH_RESULT,
  HOLD_ORDER_POOL,
  HOLD_STATUS,
  HOLD_STATUS_I18N_KEY,
  IS_ORDER_POOL_HOLDING_GRAY,
  IsOrderPoolHoldingGrayResponse,
  IsOrderPoolHoldingGrayVariables,
  LIST_ORDER_POOL_HOLDING_PAGE,
  ListOrderPoolHoldingPageResponse,
  ListOrderPoolHoldingPageVariables,
  OrderPoolHoldingPageItem,
  RESTORE_ORDER_POOL,
  type BatchAccepted,
} from '../graphql/routePlanningOrderPoolHolding';
import { getOverallStatusLabelByCode, toIsoLocalDateTimeForApi } from '../utils/overallStatusLabel';
import {
  HOLD_STATUS_ALL,
  isHoldingRowSelectable,
  isSelectAllAllowed,
  resolveHoldStatusValues,
} from '../utils/holdStatusUtils';
import WmsDownloadModal from '../../../../components/WmsDownloadModal';
import {
  DOWNLOAD_ROUTE_ORDER_POOL_EXPORT,
  DOWNLOAD_ROUTE_ORDER_POOL_EXPORT_FOR_DSP,
} from '../graphql/downloadRouteOrderPoolExport';

const { Text } = Typography;
const { TabPane } = Tabs;

export interface SplitOrderWaybillPoolDrawerProps {
  visible: boolean;
  onClose: () => void;
  initialTab?: 'all' | 'new' | 'returned';
  mode?: 'wms' | 'dsp';
}

interface TableRow {
  id: string;
  orderId?: number;
  trackingNumber: string;
  packageStatusDisplay: string;
  areaName: string;
  bagNumber: string;
  deliveryDsp: string;
  hubArrivalTime?: string;
  whsArrivalTime?: string;
  /** 以下六项仅暂存运单池页签有值 */
  holdStatus?: number;
  holdStatusDisplay?: string;
  heldAt?: string;
  heldBy?: string;
  holdRemark?: string;
  restoreResult?: string;
}

/** 顶层页签：规划运单池 / 暂存运单池（后者仅灰度可见） */
type PoolTabKey = 'pool' | 'holding';


/**
 * 受理成功后的「处理中」倒计时（秒）：每满 1000 条 5 秒，不足 1000 按 1000 算。
 *
 * 出池与入池都是后台异步跑的，受理返回时数据还没处理完；分单页上的区域包裹数、待推送汇总
 * 必须等这批跑完再刷新，否则运营拿着中间态的数字继续排线会对不上。
 * 后台 8 线程并行、单条 30~40ms，再加上每 200 条一次 100ms 的批次间隔，1 万条实测约 49 秒，
 * 与这条「每千条 5 秒」的曲线吻合；上限 50 秒对应后端的单次上限 1 万条。
 */
const COUNTDOWN_SECONDS_PER_1000 = 5;
const COUNTDOWN_MAX_SECONDS = 50;

/**
 * 按受理条数取倒计时秒数：300 → 5 秒，1120 → 10 秒，2580 → 15 秒，9880 → 50 秒。
 *
 * @param acceptedCount 后端受理的条数
 */
const resolveCountdownSeconds = (acceptedCount: number): number =>
  Math.min(
    Math.max(Math.ceil(acceptedCount / 1000), 1) * COUNTDOWN_SECONDS_PER_1000,
    COUNTDOWN_MAX_SECONDS
  );

const mapTabToParcelType = (tab: 'all' | 'new' | 'returned'): number => {
  switch (tab) {
    case 'new':
      return 1;
    case 'returned':
      return 2;
    default:
      return 0;
  }
};

type FilterFieldSnapshot = {
  waybillNumbers?: string;
  bagNumbers?: string;
  /** 区域多选，空数组表示不按区域过滤 */
  area?: string[];
  waybillStatus?: string | undefined;
  hubArrivalTime?: [string | null, string | null] | null;
  whsArrivalTime?: [string | null, string | null] | null;
};

/** 两次区域多选是否等价（顺序无关） */
const isSameAreaSelection = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) {
    return false;
  }
  const sortedB = [...b].sort();
  return [...a].sort().every((v, i) => v === sortedB[i]);
};

/** 批量输入框文本 → 去空去重的数组（运单号 / 包牌号共用） */
const splitBatchInput = (raw: string): string[] =>
  raw
    .split(/[\s,，]+/)
    .map((x) => x.trim())
    .filter(Boolean);

const SplitOrderWaybillPoolDrawer: React.FC<SplitOrderWaybillPoolDrawerProps> = ({
  visible,
  onClose,
  initialTab,
  mode,
}) => {
  const { currentWarehouse } = useWarehouseContext();
  const isDsp = mode === 'dsp';
  const { dspId: currentDspId } = useCurrentDsp();

  const isHub = currentWarehouse?.siteType === SiteType.HUB;
  const warehouseId = !isHub && currentWarehouse?.id ? Number(currentWarehouse.id) : undefined;
  const hubId =
    isHub && currentWarehouse?.id
      ? Number(currentWarehouse.id)
      : currentWarehouse?.parentSiteId
        ? Number(currentWarehouse.parentSiteId)
        : undefined;
  const effectiveWarehouseId = currentWarehouse?.id ? Number(currentWarehouse.id) : undefined;

  const { data: areasData, loading: areasLoading } = useQuery<
    GetAreasForPodReviewResponse,
    GetAreasForPodReviewVariables
  >(GET_AREAS_FOR_POD_REVIEW, {
    variables: {
      warehouseId: warehouseId || undefined,
      hubId: hubId || undefined,
    },
    skip: !visible,
    fetchPolicy: 'cache-and-network',
  });

  // ==================== 灰度：仓 + 当前账号同时命中才开放暂存能力 ====================
  // accountId 由后端从登录态取，前端只传仓；DSP 端不查、也没有该能力
  const { data: grayData } = useQuery<IsOrderPoolHoldingGrayResponse, IsOrderPoolHoldingGrayVariables>(
    IS_ORDER_POOL_HOLDING_GRAY,
    {
      variables: { warehouseId: effectiveWarehouseId as number },
      skip: !visible || isDsp || !effectiveWarehouseId,
      fetchPolicy: 'network-only',
    }
  );
  const holdingEnabled = !isDsp && (grayData?.isOrderPoolHoldingGray ?? false);

  const [waybillNumbers, setWaybillNumbers] = useState('');
  const [bagNumbers, setBagNumbers] = useState('');
  /** 区域筛选（多选），空数组表示不按区域过滤 */
  const [area, setArea] = useState<string[]>([]);
  const [waybillStatus, setWaybillStatus] = useState<string | undefined>(undefined);
  /** 暂存状态筛选，默认「暂存中」——页面默认视图，也是唯一可移回的状态 */
  const [holdStatusFilter, setHoldStatusFilter] = useState<string>(String(HOLD_STATUS.HOLDING));
  const [holdStatusDropdownOpen, setHoldStatusDropdownOpen] = useState(false);
  const [hubArrivalTime, setHubArrivalTime] = useState<[string | null, string | null] | null>(null);
  const [whsArrivalTime, setWhsArrivalTime] = useState<[string | null, string | null] | null>(null);
  const [poolTab, setPoolTab] = useState<PoolTabKey>('pool');
  const [activeTab, setActiveTab] = useState<'all' | 'new' | 'returned'>(initialTab || 'all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  /** 已应用的筛选（不含 warehouseId、parcelType、分页、dspId） */
  const [appliedFilterFields, setAppliedFilterFields] = useState<Partial<ListOrderPoolPageVariables['request']>>({});
  const [areaDropdownOpen, setAreaDropdownOpen] = useState(false);
  /** 区域下拉展开那一刻的选择，用于收起时判断「到底有没有改过」 */
  const areaOnOpenRef = useRef<string[]>([]);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  /** 勾选的 orderId（跨页保留）；selectAllMatched=true 时改为「按当前筛选全选」，不再依赖这份列表 */
  const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([]);
  const [selectAllMatched, setSelectAllMatched] = useState(false);
  const [holdModalVisible, setHoldModalVisible] = useState(false);
  const [holdRemark, setHoldRemark] = useState('');
  /** 受理成功后的「处理中」遮罩：倒计时归零再整页刷新，null 表示不在处理中 */
  const [processingSeconds, setProcessingSeconds] = useState<number | null>(null);
  const [processingText, setProcessingText] = useState('');

  const clearSelection = useCallback(() => {
    setSelectedOrderIds([]);
    setSelectAllMatched(false);
  }, []);

  /** 清空全部筛选项与勾选：两个顶层页签的数据源不同，切换时不保留上一个页签的筛选条件 */
  const clearAllFilters = useCallback(() => {
    setWaybillNumbers('');
    setBagNumbers('');
    setArea([]);
    setWaybillStatus(undefined);
    setHoldStatusFilter(String(HOLD_STATUS.HOLDING));
    setHubArrivalTime(null);
    setWhsArrivalTime(null);
    setAppliedFilterFields({});
    setCurrentPage(1);
    clearSelection();
  }, [clearSelection]);

  // 「处理中」倒计时：每秒减一，归零后整页刷新分单页（此时后台批次应已处理完）
  useEffect(() => {
    if (processingSeconds === null) {
      return;
    }
    if (processingSeconds <= 0) {
      window.location.reload();
      return;
    }
    const timer = setTimeout(() => setProcessingSeconds((prev) => (prev === null ? null : prev - 1)), 1000);
    return () => clearTimeout(timer);
  }, [processingSeconds]);

  useEffect(() => {
    if (visible) {
      setPoolTab('pool');
      setActiveTab(initialTab || 'all');
      setCurrentPage(1);
      setAppliedFilterFields({});
      setWaybillNumbers('');
      setBagNumbers('');
      setArea([]);
      setWaybillStatus(undefined);
      setHoldStatusFilter(String(HOLD_STATUS.HOLDING));
      setHubArrivalTime(null);
      setWhsArrivalTime(null);
      setHoldRemark('');
      clearSelection();
    }
  }, [visible, initialTab, clearSelection]);

  const col1Labels = [t('delivery.routes.waybillPool.label.trackingNumber'), t('delivery.routes.waybillPool.label.hubArrivalTime')];
  const col2Labels = [
    t('delivery.routes.waybillPool.label.area'),
    t('delivery.routes.waybillPool.label.whsArrivalTime'),
    // 暂存状态换行后落在同一列（「区域」正下方），纳入宽度计算才能保证输入框左边缘始终对齐
    t('delivery.routes.waybillPool.label.holdStatus'),
  ];
  const col3Labels = [t('delivery.routes.waybillPool.label.packageStatus')];
  /** 暂存状态下拉选项：「全部」+ 五个状态，顺序与状态码一致 */
  const holdStatusOptions = useMemo(
    () => [
      { label: t('common.all'), value: HOLD_STATUS_ALL },
      ...Object.values(HOLD_STATUS)
        .sort((a, b) => a - b)
        .map((code) => ({ label: t(`enum.orderPoolHoldStatus.${HOLD_STATUS_I18N_KEY[code]}`), value: String(code) })),
    ],
    []
  );

  const col1SpanClassName = getSmartLabelClassName(col1Labels);
  const col2SpanClassName = getSmartLabelClassName(col2Labels);
  const col3SpanClassName = getSmartLabelClassName(col3Labels);

  const buildExtraWith = useCallback(
    (overrides: FilterFieldSnapshot = {}): Partial<ListOrderPoolPageVariables['request']> => {
      const w = overrides.waybillNumbers !== undefined ? overrides.waybillNumbers : waybillNumbers;
      const b = overrides.bagNumbers !== undefined ? overrides.bagNumbers : bagNumbers;
      const a = overrides.area !== undefined ? overrides.area : area;
      const s = overrides.waybillStatus !== undefined ? overrides.waybillStatus : waybillStatus;
      const hub = overrides.hubArrivalTime !== undefined ? overrides.hubArrivalTime : hubArrivalTime;
      const whs = overrides.whsArrivalTime !== undefined ? overrides.whsArrivalTime : whsArrivalTime;

      const extra: Partial<ListOrderPoolPageVariables['request']> = {};
      const trackingList = splitBatchInput(w);
      if (trackingList.length > 0) {
        extra.trackingNumbers = trackingList;
      }
      const bagList = splitBatchInput(b);
      if (bagList.length > 0) {
        extra.bagNumbers = bagList;
      }
      // 区域多选：只发 areaIds，后端据此下发 IN 条件；不再发单区域的 areaId
      const areaIdList = a.map(Number).filter((v) => !Number.isNaN(v));
      if (areaIdList.length > 0) {
        extra.areaIds = areaIdList;
      }
      if (s) {
        const key = s as keyof typeof OverallStatus;
        const v = OverallStatus[key];
        if (typeof v === 'number') {
          extra.overallStatus = v;
        }
      }
      if (hub?.[0] && hub[1]) {
        extra.hubArrivalTimeStart = toIsoLocalDateTimeForApi(hub[0]);
        extra.hubArrivalTimeEnd = toIsoLocalDateTimeForApi(hub[1]);
      }
      if (whs?.[0] && whs[1]) {
        extra.whsArrivalTimeStart = toIsoLocalDateTimeForApi(whs[0]);
        extra.whsArrivalTimeEnd = toIsoLocalDateTimeForApi(whs[1]);
      }
      return extra;
    },
    [area, bagNumbers, hubArrivalTime, waybillNumbers, waybillStatus, whsArrivalTime]
  );

  const applyFiltersFromSnapshot = useCallback(
    (overrides: FilterFieldSnapshot = {}) => {
      setAppliedFilterFields(buildExtraWith(overrides));
      setCurrentPage(1);
      // 筛选条件变了，之前的勾选与「全选筛选结果」都不再对应，必须清空
      clearSelection();
    },
    [buildExtraWith, clearSelection]
  );

  useEffect(() => {
    if (visible) {
      setCurrentPage(1);
      clearSelection();
    }
  }, [activeTab, poolTab, visible, clearSelection]);

  const isHoldingTab = poolTab === 'holding';

  /**
   * 暂存状态筛选，仅暂存运单池页签有效。默认「暂存中」= 页面默认视图，也是唯一可移回的状态；
   * 选「全部」时把五个状态全量下发，避免后端把空值当成默认值。
   */
  const holdStatusValues = useMemo<number[]>(() => resolveHoldStatusValues(holdStatusFilter), [holdStatusFilter]);

  /** 当前筛选是否只含可移回状态：决定表头「全选」是否放开，避免全选出一批移不回去的单子 */
  const selectAllAllowed = useMemo(() => isSelectAllAllowed(holdStatusValues), [holdStatusValues]);

  const listVariables: ListOrderPoolPageVariables | undefined = useMemo(() => {
    if (!visible || !effectiveWarehouseId || isHoldingTab) {
      return undefined;
    }
    const request: ListOrderPoolPageVariables['request'] = {
      warehouseId: effectiveWarehouseId,
      parcelType: mapTabToParcelType(activeTab),
      pageNum: currentPage,
      pageSize,
      ...appliedFilterFields,
    };
    if (isDsp && currentDspId != null) {
      request.dspId = Number(currentDspId);
    }
    return { request };
  }, [
    visible,
    effectiveWarehouseId,
    appliedFilterFields,
    activeTab,
    currentPage,
    pageSize,
    isDsp,
    currentDspId,
    isHoldingTab,
  ]);

  /** 暂存列表筛选：与规划运单池同一套条件，去掉 parcelType / dspId（暂存页不分子页签、不给 DSP） */
  const holdingVariables: ListOrderPoolHoldingPageVariables | undefined = useMemo(() => {
    if (!visible || !effectiveWarehouseId || !isHoldingTab) {
      return undefined;
    }
    const { parcelType: _parcelType, dspId: _dspId, ...rest } = appliedFilterFields as Record<string, unknown>;
    return {
      request: {
        warehouseId: effectiveWarehouseId,
        pageNum: currentPage,
        pageSize,
        holdStatusList: holdStatusValues,
        ...rest,
      },
    } as ListOrderPoolHoldingPageVariables;
  }, [visible, effectiveWarehouseId, appliedFilterFields, currentPage, pageSize, isHoldingTab, holdStatusValues]);

  const orderPoolQuery = mode === 'wms' ? LIST_ORDER_POOL_PAGE : LIST_ORDER_POOL_PAGE_FOR_DSP;
  const orderPoolKey = mode === 'wms' ? 'listOrderPoolPage' : 'listOrderPoolPageForDsp';
  const { data: listData, loading: listLoading } = useQuery(orderPoolQuery, {
    variables: listVariables as ListOrderPoolPageVariables,
    skip: !listVariables,
    fetchPolicy: 'network-only',
  });

  const { data: holdingData, loading: holdingLoading } = useQuery<
    ListOrderPoolHoldingPageResponse,
    ListOrderPoolHoldingPageVariables
  >(LIST_ORDER_POOL_HOLDING_PAGE, {
    variables: holdingVariables as ListOrderPoolHoldingPageVariables,
    skip: !holdingVariables,
    fetchPolicy: 'network-only',
  });

  const pageResult = isHoldingTab
    ? holdingData?.listOrderPoolHoldingPage
    : (listData as any)?.[orderPoolKey];
  const totalElements = pageResult?.total ?? 0;
  const tableLoading = isHoldingTab ? holdingLoading : listLoading;

  /** 状态码 → 文案；未知状态码直接回显数字，避免新增状态时页面显示空白 */
  const holdStatusText = useCallback((code: number | null | undefined): string => {
    if (code == null) {
      return '-';
    }
    const key = HOLD_STATUS_I18N_KEY[code];
    return key ? t(`enum.orderPoolHoldStatus.${key}`) : String(code);
  }, []);

  /** 结果列：后端在「退回暂存中」和三个终态上都会写原因，有就展示 */
  const holdStatusLabel = useCallback((item: OrderPoolHoldingPageItem): string => item.failReason || '-', []);

  const tableData: TableRow[] = useMemo(() => {
    const rows = pageResult?.content ?? [];
    if (isHoldingTab) {
      return (rows as OrderPoolHoldingPageItem[]).map((item, index) => ({
        id: item.orderId != null ? String(item.orderId) : `${item.trackingNumber ?? ''}-${index}`,
        orderId: item.orderId ?? undefined,
        trackingNumber: item.trackingNumber ?? '-',
        packageStatusDisplay: getOverallStatusLabelByCode(item.overallStatus),
        areaName: item.areaName ?? '-',
        bagNumber: item.bagNumber || '-',
        deliveryDsp: item.dspName ?? '-',
        hubArrivalTime: item.hubArrivalTime ? dayjs(item.hubArrivalTime).format('YYYY-MM-DD HH:mm:ss') : undefined,
        whsArrivalTime: item.whsArrivalTime ? dayjs(item.whsArrivalTime).format('YYYY-MM-DD HH:mm:ss') : undefined,
        holdStatus: item.holdStatus ?? undefined,
        holdStatusDisplay: holdStatusText(item.holdStatus),
        heldAt: item.heldAt ? dayjs(item.heldAt).format('YYYY-MM-DD HH:mm:ss') : undefined,
        heldBy: item.heldBy || '-',
        holdRemark: item.holdRemark || '-',
        restoreResult: holdStatusLabel(item),
      }));
    }
    return (rows as OrderPoolPageItem[]).map((item, index) => ({
      id: item.orderId != null ? String(item.orderId) : `${item.trackingNumber ?? ''}-${index}`,
      orderId: item.orderId ?? undefined,
      trackingNumber: item.trackingNumber ?? '-',
      packageStatusDisplay: getOverallStatusLabelByCode(item.overallStatus),
      areaName: item.areaName ?? '-',
      bagNumber: item.bagNumber || '-',
      deliveryDsp: item.dspName ?? '-',
      hubArrivalTime: item.hubArrivalTime ? dayjs(item.hubArrivalTime).format('YYYY-MM-DD HH:mm:ss') : undefined,
      whsArrivalTime: item.whsArrivalTime ? dayjs(item.whsArrivalTime).format('YYYY-MM-DD HH:mm:ss') : undefined,
    }));
  }, [pageResult, isHoldingTab, holdStatusLabel, holdStatusText]);

  const columns: ColumnsType<TableRow> = useMemo(() => {
    const base: ColumnsType<TableRow> = [
      {
        title: t('delivery.routes.waybillPool.label.trackingNumber'),
        dataIndex: 'trackingNumber',
        key: 'trackingNumber',
        width: 200,
        fixed: 'left',
      },
      {
        title: t('delivery.routes.waybillPool.label.packageStatus'),
        dataIndex: 'packageStatusDisplay',
        key: 'packageStatusDisplay',
        width: 140,
      },
      {
        title: t('delivery.routes.waybillPool.label.area'),
        dataIndex: 'areaName',
        key: 'areaName',
        width: 100,
      },
    ];

    // 包牌号列与筛选项一样，不受暂存灰度控制，全量展示
    base.push({
      title: t('delivery.routes.waybillPool.label.bagNumber'),
      dataIndex: 'bagNumber',
      key: 'bagNumber',
      width: 140,
    });

    base.push(
      {
        title: t('delivery.routes.waybillPool.label.deliveryDsp'),
        dataIndex: 'deliveryDsp',
        key: 'deliveryDsp',
        width: 150,
      },
      {
        title: t('delivery.routes.waybillPool.label.hubArrivalTime'),
        dataIndex: 'hubArrivalTime',
        key: 'hubArrivalTime',
        width: 180,
        render: (time: string | undefined) => time || '-',
      },
      {
        title: t('delivery.routes.waybillPool.label.whsArrivalTime'),
        dataIndex: 'whsArrivalTime',
        key: 'whsArrivalTime',
        width: 180,
        render: (time: string | undefined) => time || '-',
      }
    );

    if (isHoldingTab) {
      base.push(
        {
          title: t('delivery.routes.waybillPool.label.holdStatus'),
          dataIndex: 'holdStatusDisplay',
          key: 'holdStatusDisplay',
          width: 120,
          render: (text: string | undefined) => text || '-',
        },
        {
          title: t('delivery.routes.waybillPool.label.heldAt'),
          dataIndex: 'heldAt',
          key: 'heldAt',
          width: 180,
          render: (time: string | undefined) => time || '-',
        },
        {
          title: t('delivery.routes.waybillPool.label.heldBy'),
          dataIndex: 'heldBy',
          key: 'heldBy',
          width: 120,
        },
        {
          title: t('delivery.routes.waybillPool.label.holdRemark'),
          dataIndex: 'holdRemark',
          key: 'holdRemark',
          width: 200,
        },
        {
          title: t('delivery.routes.waybillPool.label.restoreResult'),
          dataIndex: 'restoreResult',
          key: 'restoreResult',
          width: 220,
        }
      );
    }

    return base;
  }, [isHoldingTab]);

  /**
   * 行是否可勾选。暂存页只放开 10-暂存中：20/30/40 是终态、15 正在后台处理中，勾了也移不回去，
   * 后端 RESTORABLE_STATUS 同样只认 10，这里禁用是为了不让运营白选一场。
   */
  const isRowSelectable = useCallback((row: TableRow) => isHoldingRowSelectable(row, isHoldingTab), [isHoldingTab]);

  /** 当前页所有可选行的 key，用于表头全选态的回显 */
  const currentPageKeys = useMemo(
    () => tableData.filter(isRowSelectable).map((row) => row.id),
    [tableData, isRowSelectable]
  );

  const rowSelection = holdingEnabled
    ? {
        // 表头全选 = 选中「当前筛选的全部结果」（跨页），所以全选态下每页都回显为全勾
        selectedRowKeys: selectAllMatched ? currentPageKeys : selectedOrderIds.map(String),
        preserveSelectedRowKeys: true,
        onSelectAll: (selected: boolean) => {
          setSelectAllMatched(selected);
          // 全选模式按筛选条件下发，不依赖 id 列表
          setSelectedOrderIds([]);
        },
        onSelect: (record: TableRow, selected: boolean) => {
          // 单行勾选退出全选模式：此后按显式勾选的 id 列表下发，避免两种语义并存
          const current = selectAllMatched ? currentPageKeys : selectedOrderIds.map(String);
          const next = selected
            ? Array.from(new Set([...current, record.id]))
            : current.filter((key) => key !== record.id);
          setSelectAllMatched(false);
          setSelectedOrderIds(next.map(Number).filter((key) => !Number.isNaN(key)));
        },
        getCheckboxProps: (record: TableRow) => ({ disabled: !isRowSelectable(record) }),
        // 筛到终态时藏掉表头全选：跨页全选按筛选条件下发，勾上去也只会得到一批移不回去的单子
        hideSelectAll: isHoldingTab && !selectAllAllowed,
      }
    : undefined;

  const selectedCount = selectAllMatched ? totalElements : selectedOrderIds.length;

  const handleApplyFilter = () => {
    applyFiltersFromSnapshot();
  };

  const handleResetFilters = () => {
    clearAllFilters();
  };

  // ==================== 下载运单号（下载中心异步导出）====================
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadFileName, setDownloadFileName] = useState('');
  const [downloadWms, { loading: downloadWmsLoading }] = useMutation(DOWNLOAD_ROUTE_ORDER_POOL_EXPORT);
  const [downloadDsp, { loading: downloadDspLoading }] = useMutation(DOWNLOAD_ROUTE_ORDER_POOL_EXPORT_FOR_DSP);
  const downloadLoading = downloadWmsLoading || downloadDspLoading;

  const handleDownload = async () => {
    if (!listVariables) return;
    try {
      // 按当前已应用的筛选导出全部匹配（分页由后端忽略）
      if (mode === 'wms') {
        const { data } = await downloadWms({ variables: listVariables });
        const fileName = data?.downloadRouteOrderPoolExport;
        if (fileName) {
          setDownloadFileName(fileName);
          setShowDownloadModal(true);
        }
      } else {
        const { data } = await downloadDsp({ variables: listVariables });
        const fileName = data?.downloadRouteOrderPoolExportForDsp;
        if (fileName) {
          setDownloadFileName(fileName);
          setShowDownloadModal(true);
        }
      }
    } catch (error: any) {
      console.error('Download order pool tracking numbers failed:', error);
      message.error(error?.message || t('delivery.tasks.message.downloadFailed'));
    }
  };

  // ==================== 暂存 / 移回（同步受理 + 后台执行）====================
  const [holdOrderPool, { loading: holdLoading }] = useMutation(HOLD_ORDER_POOL);
  const [restoreOrderPool, { loading: restoreLoading }] = useMutation(RESTORE_ORDER_POOL);

  /**
   * 统一处理受理结果：只有 ACCEPTED 才算提交成功，其余按原因给出可操作的提示。
   * 出池 / 入池是后台异步完成的，所以这里只刷新列表，不假设数据已经变完。
   */
  const handleBatchResult = (result: BatchAccepted | undefined, isHold: boolean) => {
    if (!result) {
      message.error(t(isHold ? 'delivery.routes.waybillPool.message.holdFailed' : 'delivery.routes.waybillPool.message.restoreFailed'));
      return;
    }
    switch (result.resultCode) {
      case BATCH_RESULT.ACCEPTED:
        message.success(
          isHold
            ? t('delivery.routes.waybillPool.message.holdAccepted', {
                accepted: result.acceptedCount,
                ignored: result.ignoredCount,
              })
            : t('delivery.routes.waybillPool.message.restoreAccepted', { accepted: result.acceptedCount })
        );
        clearSelection();
        // 后台还在逐批处理，这里先进入「处理中」倒计时，结束后再整页刷新分单页，
        // 避免运营看到中间态的区域包裹数继续排线导致数量对不上
        setProcessingText(
          isHold
            ? t('delivery.routes.waybillPool.message.holdProcessing', { count: result.acceptedCount })
            : t('delivery.routes.waybillPool.message.restoreProcessing', { count: result.acceptedCount })
        );
        setProcessingSeconds(resolveCountdownSeconds(result.acceptedCount));
        break;
      case BATCH_RESULT.EXCEED_LIMIT:
        message.warning(
          t('delivery.routes.waybillPool.message.exceedLimit', {
            maxLimit: result.maxLimit,
            matched: result.matchedCount,
          })
        );
        break;
      case BATCH_RESULT.NO_MATCH:
        message.warning(t('delivery.routes.waybillPool.message.noMatch'));
        break;
      case BATCH_RESULT.GRAY_NOT_HIT:
        message.warning(t('delivery.routes.waybillPool.message.grayNotHit'));
        break;
      case BATCH_RESULT.REJECTED:
        message.warning(t('delivery.routes.waybillPool.message.busy'));
        break;
      default:
        message.error(
          t(isHold ? 'delivery.routes.waybillPool.message.holdFailed' : 'delivery.routes.waybillPool.message.restoreFailed')
        );
    }
  };

  const handleOpenHoldModal = () => {
    if (selectedCount <= 0) {
      message.warning(t('delivery.routes.waybillPool.message.selectFirst'));
      return;
    }
    setHoldRemark('');
    setHoldModalVisible(true);
  };

  const handleConfirmHold = async () => {
    if (!effectiveWarehouseId || !listVariables) return;
    try {
      const { data } = await holdOrderPool({
        variables: {
          request: selectAllMatched
            ? {
                warehouseId: effectiveWarehouseId,
                selectionMode: 'BY_FILTER',
                filter: listVariables.request,
                remark: holdRemark || null,
              }
            : {
                warehouseId: effectiveWarehouseId,
                selectionMode: 'BY_IDS',
                orderIds: selectedOrderIds,
                remark: holdRemark || null,
              },
        },
      });
      setHoldModalVisible(false);
      handleBatchResult(data?.holdOrderPool, true);
    } catch (error: any) {
      console.error('Hold order pool failed:', error);
      message.error(error?.message || t('delivery.routes.waybillPool.message.holdFailed'));
    }
  };

  const handleRestore = () => {
    if (selectedCount <= 0) {
      message.warning(t('delivery.routes.waybillPool.message.selectFirst'));
      return;
    }
    if (!effectiveWarehouseId || !holdingVariables) return;
    Modal.confirm({
      title: t('delivery.routes.waybillPool.button.restore'),
      content: t('delivery.routes.waybillPool.message.restoreConfirm', { count: selectedCount }),
      okText: t('delivery.routes.waybillPool.button.confirmRestore'),
      cancelText: t('components.button.cancel'),
      onOk: async () => {
        try {
          const { data } = await restoreOrderPool({
            variables: {
              request: selectAllMatched
                ? {
                    warehouseId: effectiveWarehouseId,
                    selectionMode: 'BY_FILTER',
                    filter: holdingVariables.request,
                  }
                : {
                    warehouseId: effectiveWarehouseId,
                    selectionMode: 'BY_IDS',
                    orderIds: selectedOrderIds,
                  },
            },
          });
          handleBatchResult(data?.restoreOrderPool, false);
        } catch (error: any) {
          console.error('Restore order pool failed:', error);
          message.error(error?.message || t('delivery.routes.waybillPool.message.restoreFailed'));
        }
      },
    });
  };

  return (
    <Drawer
      title={null}
      placement="right"
      width="calc(100vw - 304px)"
      onClose={onClose}
      open={visible}
      destroyOnClose
      closable={false}
    >
      <div className="flex flex-col h-full">
        <div className="flex items-center space-x-4 pb-4 border-b border-gray-200 mb-4">
          <Button
            type="primary"
            icon={<ArrowLeftOutlined />}
            onClick={onClose}
            className="w-auto min-w-24 bg-purple-600 border-purple-600 hover:bg-purple-700"
          >
            {t('delivery.routes.waybillPool.label.back')}
          </Button>
          {holdingEnabled ? (
            // 灰度开放后，标题位改为「规划运单池 / 暂存运单池」两个平级页签
            <Tabs
              activeKey={poolTab}
              onChange={(key) => {
                setPoolTab(key as PoolTabKey);
                // 两个页签数据源不同，切换时清空筛选与勾选，避免把上一个页签的条件带过来
                clearAllFilters();
              }}
              className="mb-0"
              items={[
                { key: 'pool', label: t('delivery.routes.waybillPool.label.title') },
                { key: 'holding', label: t('delivery.routes.waybillPool.label.holdPoolTitle') },
              ]}
            />
          ) : (
            <div className="flex items-center">
              <span className="h-5 border-l-4 border-purple-500 mr-2 mt-1" />
              <span className="text-xl font-medium text-black">{t('delivery.routes.waybillPool.label.title')}</span>
            </div>
          )}
        </div>

        <div className="mb-2 py-2">
          <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start mb-4">
            <div className="min-w-0 col-span-3">
              <BatchInputFilterItem
                label={t('delivery.routes.waybillPool.label.trackingNumber')}
                value={waybillNumbers}
                onChange={(e) => setWaybillNumbers(e.target.value)}
                onBlur={() => applyFiltersFromSnapshot()}
                spanClassName={col1SpanClassName}
                rows={2}
                placeholder={t('delivery.components.enterMultipleTrackingNumber')}
              />
            </div>
          </div>

          {/* 包牌号：与运单号同款整行大输入框，支持逗号/换行分隔；不受暂存灰度控制，全量生效 */}
          <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start mb-4">
            <div className="min-w-0 col-span-3">
              <BatchInputFilterItem
                label={t('delivery.routes.waybillPool.label.bagNumber')}
                value={bagNumbers}
                onChange={(e) => setBagNumbers(e.target.value)}
                onBlur={() => applyFiltersFromSnapshot()}
                spanClassName={col1SpanClassName}
                rows={2}
                placeholder={t('delivery.routes.waybillPool.placeholder.bagNumber')}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-x-10 gap-y-4 items-start mb-4">
            <div className="min-w-0">
              <DateTimeRangeFilterItem
                label={t('delivery.routes.waybillPool.label.hubArrivalTime')}
                value={hubArrivalTime}
                onChange={(next) => {
                  setHubArrivalTime(next);
                  applyFiltersFromSnapshot({ hubArrivalTime: next });
                }}
                spanClassName={col1SpanClassName}
              />
            </div>
            <div className="min-w-0">
              <DateTimeRangeFilterItem
                label={t('delivery.routes.waybillPool.label.whsArrivalTime')}
                value={whsArrivalTime}
                onChange={(next) => {
                  setWhsArrivalTime(next);
                  applyFiltersFromSnapshot({ whsArrivalTime: next });
                }}
                spanClassName={col2SpanClassName}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-x-10 gap-y-4 items-start">
            <div className="min-w-0">
              <ParcelFilterStyle
                label={t('delivery.routes.waybillPool.label.area')}
                mode="multiple"
                maxTagCount="responsive"
                placeholder={t('delivery.routes.waybillPool.placeholder.area')}
                value={area}
                open={areaDropdownOpen}
                onDropdownVisibleChange={(open) => {
                  setAreaDropdownOpen(open);
                  if (open) {
                    areaOnOpenRef.current = area;
                    return;
                  }
                  // 多选下每勾一个就请求一次没有意义，收起下拉时再按最终选择发一次。
                  // 选择没变就什么都不做：applyFiltersFromSnapshot 会清空勾选，
                  // 不能让「随手点开又收起区域下拉」把运营已勾的几百条运单弄丢
                  if (!isSameAreaSelection(areaOnOpenRef.current, area)) {
                    applyFiltersFromSnapshot();
                  }
                }}
                onChange={(value) => {
                  const next = Array.isArray(value) ? value.map(String) : [];
                  setArea(next);
                  // 下拉展开时只记选中项、不请求，让运营一次勾完多个区域，收起时再统一筛选；
                  // 下拉收起时的 onChange 只可能来自「清空」或「删某个 tag」，这两种要立即生效
                  if (!areaDropdownOpen) {
                    applyFiltersFromSnapshot({ area: next });
                  }
                }}
                allowClear
                loading={areasLoading}
                showSearch
                spanClassName={col2SpanClassName}
                options={
                  areasData?.getAreasForPodReview?.map((a) => ({
                    label: a.name,
                    value: a.id,
                  })) || []
                }
              />
            </div>
            <div className="min-w-0">
              <ParcelFilterStyle
                label={t('delivery.routes.waybillPool.label.packageStatus')}
                mode={undefined}
                placeholder={t('delivery.routes.waybillPool.placeholder.waybillStatus')}
                value={waybillStatus}
                open={statusDropdownOpen}
                onDropdownVisibleChange={setStatusDropdownOpen}
                onChange={(value) => {
                  const next = value ? String(value) : undefined;
                  setWaybillStatus(next);
                  applyFiltersFromSnapshot({ waybillStatus: next });
                  setStatusDropdownOpen(false);
                }}
                allowClear
                showSearch
                spanClassName={col3SpanClassName}
                options={overallStatusOptions.map((option) => ({
                  label: option.label,
                  value: option.value,
                }))}
              />
            </div>
            {isHoldingTab && (
              <div className="min-w-0">
                <ParcelFilterStyle
                  label={t('delivery.routes.waybillPool.label.holdStatus')}
                  mode={undefined}
                  placeholder={t('delivery.routes.waybillPool.placeholder.holdStatus')}
                  value={holdStatusFilter}
                  open={holdStatusDropdownOpen}
                  onDropdownVisibleChange={setHoldStatusDropdownOpen}
                  onChange={(value) => {
                    // 状态一变，已勾选的行可能已不可选，直接清空勾选避免把终态单子带进移回请求
                    setHoldStatusFilter(value ? String(value) : String(HOLD_STATUS.HOLDING));
                    setCurrentPage(1);
                    clearSelection();
                    setHoldStatusDropdownOpen(false);
                  }}
                  showSearch
                  spanClassName={col2SpanClassName}
                  options={holdStatusOptions}
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-4 mt-4">
            {holdingEnabled && !isHoldingTab && (
              <Button
                type="primary"
                onClick={handleOpenHoldModal}
                loading={holdLoading}
                disabled={selectedCount === 0}
                className="min-w-24 font-semibold"
              >
                {t('delivery.routes.waybillPool.button.hold')}
              </Button>
            )}
            {holdingEnabled && isHoldingTab && (
              <Button
                type="primary"
                onClick={handleRestore}
                loading={restoreLoading}
                disabled={selectedCount === 0}
                className="min-w-24 font-semibold"
              >
                {t('delivery.routes.waybillPool.button.restore')}
              </Button>
            )}
            {!isHoldingTab && (
              <Button
                type="primary"
                onClick={handleDownload}
                loading={downloadLoading}
                disabled={totalElements === 0}
                className="min-w-24 font-semibold"
              >
                {t('delivery.tasks.label.download')}
              </Button>
            )}
            <Button type="primary" onClick={handleApplyFilter} className="min-w-24 font-semibold">
              {t('components.button.filter')}
            </Button>
            <Button onClick={handleResetFilters} className="min-w-24 font-semibold">
              {t('components.button.reset')}
            </Button>
          </div>
        </div>

        {!isHoldingTab && (
          <div className="mb-4">
            <Tabs activeKey={activeTab} onChange={(key) => setActiveTab(key as 'all' | 'new' | 'returned')}>
              <TabPane tab={t('delivery.routes.waybillPool.tab.all')} key="all" />
              <TabPane tab={t('delivery.routes.waybillPool.tab.new')} key="new" />
              <TabPane tab={t('delivery.routes.waybillPool.tab.returned')} key="returned" />
            </Tabs>
          </div>
        )}

        <div className="mb-3 flex items-center gap-4">
          <Text strong className="text-gray-800">
            {isHoldingTab
              ? t('delivery.routes.waybillPool.label.holdCount')
              : t('delivery.routes.waybillPool.label.waybillCount')}
            : {totalElements}
          </Text>
          {holdingEnabled && selectedCount > 0 && (
            <Text className="text-gray-600">
              {t('delivery.routes.waybillPool.label.selectedCount', { count: selectedCount })}
            </Text>
          )}
        </div>

        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="flex-1 overflow-auto">
            <Table<TableRow>
              columns={columns}
              dataSource={tableData}
              loading={tableLoading}
              rowKey="id"
              rowSelection={rowSelection}
              pagination={false}
              scroll={{ x: 'max-content' }}
              className="mb-4"
            />
          </div>
          <div className="flex justify-end border-t pt-4">
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalElements}
              onChange={(page, size) => {
                setCurrentPage(page);
                setPageSize(size || 20);
              }}
              showSizeChanger
              showQuickJumper
              showTotal={(total) => t('table.pagination.totalItems', { total })}
            />
          </div>
        </div>
      </div>

      {/* 处理中遮罩：不可关闭，倒计时结束自动刷新页面，避免运营中途拿到中间态数据去排线 */}
      <Modal
        open={processingSeconds !== null}
        title={t('delivery.routes.waybillPool.label.processingTitle')}
        closable={false}
        maskClosable={false}
        keyboard={false}
        footer={null}
      >
        <div className="flex items-center gap-3 py-2">
          <Spin />
          <div>
            <div>{processingText}</div>
            <div className="text-gray-500 mt-1">
              {t('delivery.routes.waybillPool.message.refreshCountdown', { seconds: processingSeconds ?? 0 })}
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={holdModalVisible}
        title={t('delivery.routes.waybillPool.button.hold')}
        okText={t('delivery.routes.waybillPool.button.confirmHold')}
        cancelText={t('components.button.cancel')}
        confirmLoading={holdLoading}
        onOk={handleConfirmHold}
        onCancel={() => setHoldModalVisible(false)}
      >
        <p className="mb-3">
          {t('delivery.routes.waybillPool.message.holdConfirm', { count: selectedCount })}
        </p>
        <div className="mb-1 text-gray-600">{t('delivery.routes.waybillPool.label.holdRemark')}</div>
        <Input.TextArea
          rows={2}
          maxLength={200}
          value={holdRemark}
          onChange={(e) => setHoldRemark(e.target.value)}
          placeholder={t('delivery.routes.waybillPool.placeholder.holdRemark')}
        />
      </Modal>

      <WmsDownloadModal
        visible={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        fileName={downloadFileName}
        downloadPagePath={mode === 'wms' ? '/wms/download' : '/delivery/download'}
      />
    </Drawer>
  );
};

export default SplitOrderWaybillPoolDrawer;
